import Phaser from "phaser";
import { GAME_CONFIG } from "../config";
import { LevelData, LEVELS } from "../data/levelData";
import { AutomataCoordinator } from "../systems/AutomataCoordinator";
import {
  createGameStateManager,
  GameStateManagerState,
  hasWon as gameStateHasWon,
  transitionTo,
} from "../systems/GameStateManager";
import { GlobalState } from "../systems/GlobalState";
import { InputSystem } from "../systems/InputSystem";
import { TextureLoader } from "../tiles/TextureLoader";
import { TileIndex, Vector } from "../types";
import {
  createDebugUI,
  DebugUIManagerState,
  destroyDebugUI,
  updateDebugInfo,
} from "../ui/DebugUIManager";
import {
  createScreenOverlayManager,
  hideStartScreen,
  ScreenOverlayState,
  showGameOverScreen as showGameOverScreenOverlay,
  showStartScreen as showStartScreenOverlay,
  showWinScreen as showWinScreenOverlay,
} from "../ui/ScreenOverlayManager";
import { StatsPanel } from "../ui/StatsPanelManager";
import { logger } from "../utils/logger";
import { TILE } from "../constants/gameConstants";

// Entity managers fully migrated to AutomataCoordinator

// Properly typed stubs for compatibility
type Ball = { col: number; row: number; stuckOnArrow?: boolean; stuckDirection?: Vector;[key: string]: any };
type WaterBlock = { col: number; row: number;[key: string]: any };

// Entity management fully migrated to AutomataCoordinator + InputSystem

export class GameScene extends Phaser.Scene {
  private static currentLevelIndex = 0;
  private cursors: null = null;
  private statsPanelManager: StatsPanel | null = null;
  private screenOverlayManager: ScreenOverlayState | null = null;
  private gameStateManager: GameStateManagerState | null = null;
  private isWaitingToStart = true;
  private currentLevel: TileIndex[][] = [];
  // entityMap is now a getter that queries the coordinator's currentEntityMap
  // This provides backward compatibility while centralizing state in coordinator
  private get entityMap(): TileIndex[][] {
    return this.coordinator?.getCurrentEntityMap() || [];
  }
  private levelWidth = 0;
  private levelHeight = 0;
  private isCustomLevel = false;
  // movementState, pathState, isOnArrowTile, arrowMoveQueue removed - click-to-move feature deleted
  private debugUIManager: DebugUIManagerState | null = null;
  private isDebugMode = false;
  private isShowingHint = false;
  private coordinator: AutomataCoordinator | null = null;
  private globalState: GlobalState | null = null;
  private inputSystem: InputSystem | null = null;

  constructor() {
    super({ key: "GameScene" });
  }

  preload(): void {
    // Load all PNG textures using TextureLoader
    TextureLoader.loadImages(this);
  }

  create(): void {
    this.gameStateManager = createGameStateManager();
    this.isWaitingToStart = true;
    this.isShowingHint = false;

    // Check for starting level index (from password or home screen)
    const startLevelIndex = (window as any).__startLevelIndex as
      | number
      | undefined;
    if (startLevelIndex !== undefined) {
      GameScene.currentLevelIndex = startLevelIndex;
      delete (window as any).__startLevelIndex;
    }

    // Check for custom level from URL
    const customLevel = (window as any).__customLevel as LevelData | undefined;
    let levelData: LevelData;

    if (customLevel && GameScene.currentLevelIndex === 0) {
      levelData = customLevel;
      this.isCustomLevel = true;
    } else {
      levelData = LEVELS[GameScene.currentLevelIndex];
      this.isCustomLevel = false;
    }

    // Deep copy level data
    this.currentLevel = levelData.tiles.map((row) => [...row]);

    // Calculate level dimensions
    this.levelHeight = this.currentLevel.length;
    this.levelWidth = this.currentLevel[0]?.length || GAME_CONFIG.gridWidth;

    // entityMap is now a getter that queries coordinator.getCurrentEntityMap()

    // Calculate world size (full level)
    const worldWidth = GAME_CONFIG.tileSize * this.levelWidth;
    const worldHeight = GAME_CONFIG.tileSize * this.levelHeight;

    // Calculate viewport size (max 10x10 tiles)
    const maxViewTiles = GAME_CONFIG.viewportSize;
    const viewportWidth =
      GAME_CONFIG.tileSize * Math.min(this.levelWidth, maxViewTiles);
    const viewportHeight =
      GAME_CONFIG.tileSize * Math.min(this.levelHeight, maxViewTiles);

    // Resize game canvas to viewport size + stats panel
    const totalWidth = viewportWidth + GAME_CONFIG.statsPanelWidth;
    this.scale.resize(totalWidth, viewportHeight);

    // Initialize screen overlay manager with original viewport dimensions
    this.screenOverlayManager = createScreenOverlayManager(
      totalWidth,
      viewportHeight
    );

    // Set up physics world bounds
    this.physics.world.setBounds(0, 0, worldWidth, worldHeight);

    // Count stars in level for GlobalState
    const totalStars = this.countStarsInLevel(this.currentLevel);

    // Create global game state
    this.globalState = new GlobalState(totalStars);

    // Create cellular automata coordinator with callbacks
    this.coordinator = new AutomataCoordinator(
      this,
      this.currentLevel,
      this.globalState,
      {
        onPlayerDeath: () => this.handleDeath(),
        onLevelWin: () => this.handleWin(),
        onShowHint: (hintText: string) => this.handleShowHint(hintText),
        onHideHint: () => this.handleHideHint(),
      },
      levelData.hint
    );

    // Create input system (handles both keyboard input and autonomous entity ticks)
    this.inputSystem = new InputSystem(
      this,
      this.coordinator,
      GAME_CONFIG.inputRepeatDelay,
      50 // 100ms = 10 ticks/second for autonomous entities
    );

    // Set up physics world bounds
    this.physics.world.setBounds(0, 0, worldWidth, worldHeight);

    // Set up camera to follow player (coordinator calculates all dimensions)
    this.coordinator.setupCamera(this.cameras.main);

    this.createUI();

    // Set up debug button click handler
    if (this.statsPanelManager?.debugButton) {
      this.statsPanelManager.debugButton.on("pointerdown", () => {
        this.toggleDebugMode();
      });
    }

    // Set up copy button click handler
    if (this.statsPanelManager?.copyButton) {
      this.statsPanelManager.copyButton.on("pointerdown", () => {
        this.copyLevelToClipboard();
      });
    }

    // Click-to-move removed - using CA system with keyboard only

    this.showStartScreen({ levelName: levelData.name, levelPassword: levelData.password });
  }

  private createUI(): void {
    // Use viewport size for UI positioning
    const maxViewTiles = GAME_CONFIG.viewportSize;
    const viewportWidth =
      GAME_CONFIG.tileSize * Math.min(this.levelWidth, maxViewTiles);
    const viewportHeight =
      GAME_CONFIG.tileSize * Math.min(this.levelHeight, maxViewTiles);
    const panelWidth = GAME_CONFIG.statsPanelWidth;

    const remaining = this.globalState
      ? this.globalState.totalStars - this.globalState.starsCollected
      : 0;

    this.statsPanelManager = new StatsPanel(this, {
      viewportWidth,
      viewportHeight,
      panelWidth,
      levelNumber: GameScene.currentLevelIndex + 1,
      starsRemaining: remaining,
      isCustomLevel: this.isCustomLevel,
      onRestart: () => this.scene.restart(),
      levelWidth: this.levelWidth,
      levelHeight: this.levelHeight,
      globalState: this.globalState,
    });
  }

  private showStartScreen({ levelName, levelPassword }: { levelName: string; levelPassword?: string }): void {
    if (!this.screenOverlayManager) return;

    showStartScreenOverlay(this, this.screenOverlayManager, {
      levelName,
      levelNumber: GameScene.currentLevelIndex + 1,
      password: levelPassword,
      isCustomLevel: this.isCustomLevel,
      onStart: () => {
        if (this.isWaitingToStart) {
          this.startLevel();
        }
      },
    });
  }

  private startLevel(): void {
    if (!this.isWaitingToStart || !this.screenOverlayManager) return;

    this.isWaitingToStart = false;
    if (this.gameStateManager) {
      transitionTo(this.gameStateManager, "playing", "level started");
    }
    if (this.inputSystem) {
      this.inputSystem.resume();
    }

    hideStartScreen(
      this,
      this.screenOverlayManager,
    );

    // Re-setup camera after hiding start screen
    if (this.coordinator) {
      this.coordinator.setupCamera(this.cameras.main);
    }
  }

  private handleDeath(): void {
    // Guard against multiple calls - check if already in dead state
    if (this.gameStateManager && this.gameStateManager.currentState === "dead") {
      return;
    }

    if (this.gameStateManager) {
      transitionTo(this.gameStateManager, "dead", "player died");
    }

    if (this.inputSystem) {
      this.inputSystem.pause();
    }

    // Show game over screen after explosion animation (800ms)
    this.time.delayedCall(800, () => {
      this.showGameOverScreen();
    });
  }

  private showGameOverScreen(): void {
    // Use viewport size for UI
    const maxViewTiles = GAME_CONFIG.viewportSize;
    const viewportWidth =
      GAME_CONFIG.tileSize * Math.min(this.levelWidth, maxViewTiles);
    const viewportHeight =
      GAME_CONFIG.tileSize * Math.min(this.levelHeight, maxViewTiles);
    const totalWidth = viewportWidth + GAME_CONFIG.statsPanelWidth;

    showGameOverScreenOverlay(this, {
      viewportWidth,
      viewportHeight,
      totalWidth,
      onRestart: () => this.scene.restart(),
    });
  }

  private handleWin(): void {
    // Guard against multiple calls - check if already in won state
    if (this.gameStateManager && gameStateHasWon(this.gameStateManager)) return;

    if (this.gameStateManager) {
      transitionTo(this.gameStateManager, "won", "level completed");
    }

    // Pause all entity movement via InputSystem
    if (this.inputSystem) {
      this.inputSystem.pause();
    }

    this.showWinScreen();
  }

  private showWinScreen(): void {
    const isLastLevel =
      !this.isCustomLevel && GameScene.currentLevelIndex >= LEVELS.length - 1;

    if (!this.screenOverlayManager) return;

    showWinScreenOverlay(this, {
      levelNumber: GameScene.currentLevelIndex + 1,
      starsCollected: this.globalState?.starsCollected ?? 0,
      isLastLevel,
      onNextLevel: () => {
        // Resize back to gameplay dimensions before restarting
        if (this.screenOverlayManager) {
          this.scale.resize(
            this.screenOverlayManager.originalViewportWidth,
            this.screenOverlayManager.originalViewportHeight
          );
        }

        if (!this.isCustomLevel) {
          GameScene.currentLevelIndex++;
        }
        this.scene.restart();
      },
      onPlayAgain: () => {
        GameScene.currentLevelIndex = 0;
        this.scene.restart();
      },
    });
  }

  private handleShowHint(hintText: string): void {
    if (!this.statsPanelManager) return;

    this.isShowingHint = true;
    this.statsPanelManager.showHint(hintText);
  }

  private handleHideHint(): void {
    if (!this.statsPanelManager) return;

    this.isShowingHint = false;
    this.statsPanelManager.hideHint();
  }

  update(time: number): void {
    // Update stats panel
    if (this.statsPanelManager) {
      this.statsPanelManager.update();
    }

    // Update debug info display if active
    if (this.isDebugMode && this.debugUIManager && this.coordinator) {
      updateDebugInfo(this.debugUIManager, {
        hasWon: this.coordinator.getWinState(),
        isWaitingToStart: this.isWaitingToStart,
        isShowingHint: this.isShowingHint,
        isCustomLevel: this.isCustomLevel,
      });
    }

    // Handle input via InputSystem (no need to pass player - InputSystem handles it)
    if (this.isWaitingToStart) {
      return;
    }

    if (this.inputSystem && this.coordinator) {
      // Check for keyboard input (InputSystem calls processTick internally)
      this.inputSystem.update(time);
    }
  }

  private copyLevelToClipboard(): void {
    // Convert tilemap to formatted JSON string
    const tilemapJson = JSON.stringify(this.currentLevel, null, 2);

    // Try to copy to clipboard
    navigator.clipboard
      .writeText(tilemapJson)
      .then(() => {
        console.log("Tilemap copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy:", err);
        // Fallback: show in prompt for manual copy
        prompt("Copy this tilemap:", tilemapJson);
      });
  }

  private toggleDebugMode(): void {
    if (this.isDebugMode) {
      // Turn off debug mode
      if (this.debugUIManager) {
        destroyDebugUI(this.debugUIManager);
        this.debugUIManager = null;
      }
      this.isDebugMode = false;

      // Resume entity movement via InputSystem
      if (this.inputSystem) {
        this.inputSystem.resume();
      }

      // Show normal stats panel
      if (this.statsPanelManager) {
        this.statsPanelManager.showContent();
      }
    } else {
      console.log(this.globalState);
      // Turn on debug mode
      const maxViewTiles = GAME_CONFIG.viewportSize;
      const viewportWidth =
        GAME_CONFIG.tileSize * Math.min(this.levelWidth, maxViewTiles);
      const viewportHeight =
        GAME_CONFIG.tileSize * Math.min(this.levelHeight, maxViewTiles);

      // Pause entity movement via InputSystem
      if (this.inputSystem) {
        this.inputSystem.pause();
      }

      // Gather debug info
      const debugInfo = {
        hasWon: this.coordinator?.getWinState() ?? false,
        isWaitingToStart: this.isWaitingToStart,
        isShowingHint: this.isShowingHint,
        isCustomLevel: this.isCustomLevel,
        cursors: this.cursors,
      };

      // Hide normal stats panel
      if (this.statsPanelManager) {
        this.statsPanelManager.hideContent();
      }

      // Create debug UI
      this.debugUIManager = createDebugUI(this, {
        viewportWidth,
        viewportHeight,
        panelWidth: GAME_CONFIG.statsPanelWidth,
        panelX: viewportWidth,
        levelData: this.currentLevel,
        entityMap: this.entityMap,
        tileSize: GAME_CONFIG.tileSize,
        debugInfo,
      });

      this.isDebugMode = true;
    }
  }

  private countStarsInLevel(level: TileIndex[][]): number {
    let count = 0;
    for (const row of level) {
      for (const tile of row) {
        if (tile === TILE.STAR) {
          count++;
        }
      }
    }
    return count;
  }
}
